/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, PageGridContainer, breakpoints } from '@coursera/cds-core';

import HeroBadges, {
  mapPageDataToHeroBadges,
} from 'bundles/unified-description-page-common/components/modules/hero/HeroBadges';
import { PAGE_CONTENT_CONTAINER_NAME } from 'bundles/unified-description-page-common/constants';
import { useXDPRedesignPhase1Data } from 'bundles/unified-description-page-common/providers/specialization/XDPRedesignPhase1Provider';
import type { ConsumerCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerCoursePageQuery';
import type { ConsumerSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationPageQuery';
import type { EnterpriseCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseCoursePageQuery';
import type { EnterpriseSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseSpecializationPageQuery';

const originalStyles = {
  section: css`
    background-color: var(--cds-color-neutral-background-primary);
    min-height: 44px;
    display: flex;
    align-items: center;
    ${breakpoints.down('xs')} {
      min-height: 64px;
    }

    /* We are iterating quickly on a follow-up after negative results, hacking solution to changing color. */
    .cds-breadcrumbs-listItem > a,
    .cds-breadcrumbs-listItem button > svg {
      color: var(--cds-color-neutral-primary-weak) !important;
    }

    .cds-breadcrumbs-list {
      align-items: center;
    }
  `,
  pageGridContainerOverrides: css`
    > div {
      margin-top: 0;
      margin-bottom: 0;
    }
  `,
  breadcrumbs: css`
    white-space: nowrap;
  `,
  // Placeholder for the redesign styles object shape parity.
  ctaContainer: css`
    background: inherit;
  `,
};

const redesignStyles = {
  section: css`
    background-color: transparent;
    display: flex;
    align-items: center;
    padding-top: var(--cds-spacing-50);

    /* We are iterating quickly on a follow-up after negative results, hacking solution to changing color. */
    .cds-breadcrumbs-listItem > a,
    .cds-breadcrumbs-listItem button > svg {
      color: var(--cds-color-neutral-primary-weak) !important;
    }

    .cds-breadcrumbs-list {
      align-items: center;
    }

    nav {
      margin-left: 0;

      ol,
      ul {
        margin-top: 0 !important;
        margin-bottom: 0 !important;
      }
    }
  `,
  pageGridContainerOverrides: css`
    > div {
      margin-top: 0;
      margin-bottom: 0;
    }
  `,
  breadcrumbs: css`
    white-space: nowrap;
  `,
  ctaContainer: css`
    flex-wrap: nowrap;
  `,
  heroBadges: css`
    display: block;
    position: relative;
    z-index: 1;

    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
};

const twoPanelStyles = {
  // Negative margin extends the bg to the content-track edges (covering the hero bleed below);
  // matching padding preserves inner alignment.
  section: css`
    @container ${PAGE_CONTENT_CONTAINER_NAME} (min-width: 0) {
      margin-inline-start: calc(-1 * var(--xdp-content-gutter-inline-start, 0));
      margin-inline-end: calc(-1 * var(--xdp-content-gutter-inline-end, 0));
      padding-inline-start: var(--xdp-content-gutter-inline-start, 0);
      padding-inline-end: var(--xdp-content-gutter-inline-end, 0);
    }
  `,
};

type Props = {
  /** Required for XDP pages in order to render the hero badges. Not required for other pages. */
  pageData?:
    | ConsumerSpecializationPageFieldsFragment
    | ConsumerCoursePageFieldsFragment
    | EnterpriseSpecializationPageFieldsFragment
    | EnterpriseCoursePageFieldsFragment;
  /** Breadcrumb navigation elements to display. */
  breadcrumbs?: React.ReactNode;
  /** Array of call-to-action elements to display. */
  ctas?: Array<React.ReactNode>;
};

/**
 * BreadcrumbsModule component that displays breadcrumb navigation and call-to-action elements.
 *
 * This component renders a section containing breadcrumbs on the left and CTAs on the right.
 * For XDP pages, it also displays hero badges when pageData is provided.
 *
 * @param props - Component props
 * @param props.pageData - Optional page data required for XDP pages to render hero badges
 * @param props.breadcrumbs - Optional breadcrumb navigation elements
 * @param props.ctas - Optional array of call-to-action elements
 * @returns JSX element or null if no breadcrumbs or CTAs are provided
 */
const BreadcrumbsModule = ({ breadcrumbs, ctas, pageData }: Props) => {
  const { enableXDPRedesignPhase1, enableXDPRedesignHero } = useXDPRedesignPhase1Data();
  const enableXDPRedesign = enableXDPRedesignPhase1 || enableXDPRedesignHero;
  const styles = enableXDPRedesign ? redesignStyles : originalStyles;

  if (!breadcrumbs && !ctas) {
    return null;
  }

  return (
    <section css={[styles.section, twoPanelStyles.section]}>
      <PageGridContainer css={styles.pageGridContainerOverrides}>
        <Grid
          item
          container
          xs={12}
          style={{ paddingBottom: 0, paddingTop: 0 }}
          alignItems="center"
          justifyContent="space-between"
        >
          <Grid item xs={8} css={styles.breadcrumbs}>
            {breadcrumbs}
          </Grid>
          <Grid item container xs={4} justifyContent="flex-end" css={styles.ctaContainer}>
            {pageData && (
              <div css={redesignStyles.heroBadges}>
                <HeroBadges {...mapPageDataToHeroBadges(pageData)} />
              </div>
            )}
            {ctas}
          </Grid>
        </Grid>
      </PageGridContainer>
    </section>
  );
};

export default BreadcrumbsModule;
